import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'lastManualRefreshAt';

/**
 * Rate-limits full page reloads to once per `cooldownMs` (default 30s).
 *
 * The last-refresh timestamp lives in localStorage rather than React state:
 * a "refresh" IS a page reload, which wipes any in-memory state immediately,
 * so component state alone can't stop someone from tapping again right after
 * the reload completes — only something that survives the reload can.
 *
 * @param {number} cooldownMs
 * @returns {{ ready: boolean, secondsRemaining: number, refresh: () => boolean }}
 */
function useThrottledReload(cooldownMs = 30000) {
    const getRemainingMs = useCallback(() => {
        let last = 0;
        try {
            last = Number(localStorage.getItem(STORAGE_KEY)) || 0;
        } catch (error) {
            // Storage unavailable (private mode, quota) — treat as never refreshed.
        }
        return Math.max(0, cooldownMs - (Date.now() - last));
    }, [cooldownMs]);

    const [remainingMs, setRemainingMs] = useState(getRemainingMs);

    useEffect(() => {
        if (remainingMs <= 0) return;
        const intervalId = setInterval(() => setRemainingMs(getRemainingMs()), 1000);
        return () => clearInterval(intervalId);
    }, [remainingMs, getRemainingMs]);

    const refresh = useCallback(() => {
        if (getRemainingMs() > 0) return false;
        try {
            localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch (error) {
            // Storage unavailable — still allow the reload, just without the throttle.
        }
        window.location.reload();
        return true;
    }, [getRemainingMs]);

    return {
        ready: remainingMs <= 0,
        secondsRemaining: Math.ceil(remainingMs / 1000),
        refresh,
    };
}

export default useThrottledReload;
